/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RejectAddVcDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 20-03-2025
 * @description : Component to add vehicle components.
 */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { adminSettingsAPI, HandleApiActions } from "../../redux/actions";
import { Box, Stack } from "@mui/material";
import CustomDialogBoxV2 from "../customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";

function RejectAddVcDialog({
  openDialog,
  setOpenDialog,
  title,
  selectedAddVcId,
  setSelectedAddVcId,
  getVcAddRequests,
}) {
  /**
   * ===================== State Initialization =====================
   */
  /** ============== Redux ============== **/
  const dispatch = useDispatch();
  /** ============== Reject Add Vehicle Component Dialog ============== **/
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  /**
   * ===================== Effect Hooks =====================
   */

  /**
   * ===================== API Call Functions =====================
   */
  /**
   * @author : Narender
   * @Date : 20-03-2025
   * @description : Reject add vehicle component request
   * @param {id, reason}
   * @return {msg: "", data: [], error: true | false}
   */
  const rejectAddVCRequest = async () => {
    const apiDetails = adminSettingsAPI.reject_add_vc_request;
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: true,
        params: {
          id: Number(selectedAddVcId),
          reason,
        },
      })
    );
    if (!resp.error) {
      handleCloseDialog();
      getVcAddRequests();
    }
  };

  /**
   * ===================== UI & State Management Functions =====================
   */
  // Validates the fields in the dialog
  const isDialogFieldsValid = () => {
    return reason?.trim() !== "" && !reasonError;
  };
  // Clears all input fields in the dialog
  // and also clear setSelectedAddVcId
  const clearDialogFields = () => {
    setReason("");
    setReasonError("");
    setSelectedAddVcId(null);
  };
  // Handles closing the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    clearDialogFields();
  };

  return (
    <CustomDialogBoxV2
      open={openDialog}
      onClose={handleCloseDialog}
      title={title}
      onDisabled={!isDialogFieldsValid()}
      successButtonName={"Confirm"}
      rejectButtonName={"Close"}
      onClickSuccess={rejectAddVCRequest}
      actionButtonStyle={"continuous"}
      onClickReject={handleCloseDialog}
    >
      <Stack spacing={1.2} sx={{ width: "350px" }}>
        <Box>
          <CustomFormTextFieldV2
            required={true}
            label={"Reason"}
            value={reason}
            setValue={(val) => {
              if (val.length <= 100) {
                setReason(val);
              }
            }}
            maxLength={100}
          />
        </Box>
      </Stack>
    </CustomDialogBoxV2>
  );
}

export default RejectAddVcDialog;

// Styles
const styles = {
  txtStyle: {
    width: "100%",
  },
};
